import {
  useV2ListSpecialConditionsForVendorQuery,
  useV2CreateSpecialConditionForVendorMutation,
  useV2UpdateVendorSpecialConditionMutation,
} from "@generated";
import type { V2SpecialConditionResource } from "@generated";

export type SpecialConditionStatus = "active" | "skipped" | "optional";

export interface SpecialConditionOverride {
  id: string;
  serviceConditionId: string;
  content: string;
  status: SpecialConditionStatus;
}

// A vendor's special-condition overrides, plus create/update helpers.
export const useVendorSpecialConditions = (vendorId: string) => {
  const { data, isLoading, refetch } = useV2ListSpecialConditionsForVendorQuery(
    {
      vendorId: vendorId,
      // Include the service-condition so each override can be matched to the
      // condition it overrides.
      include: ["service-condition"],
      page: { size: 100 },
    },
    { refetchOnMountOrArgChange: true }
  );

  const overrides: SpecialConditionOverride[] = (data?.data ?? []).map(
    (sc) => ({
      id: String(sc.id),
      serviceConditionId: String(
        sc.relationships?.["service-condition"]?.data?.id ?? ""
      ),
      content: sc.attributes?.content ?? "",
      status: (sc.attributes?.status ?? "active") as SpecialConditionStatus,
    })
  );

  const [create] = useV2CreateSpecialConditionForVendorMutation();
  const [update] = useV2UpdateVendorSpecialConditionMutation();

  const createOverride = (
    serviceConditionId: string,
    content: string,
    status: SpecialConditionStatus
  ) =>
    create({
      vendorId: vendorId,
      body: {
        data: {
          type: "special-conditions",
          attributes: { content, status },
          relationships: {
            "service-condition": {
              data: {
                type: "service-conditions",
                id: serviceConditionId,
              },
            },
          },
        } as unknown as V2SpecialConditionResource,
      },
    }).unwrap();

  const updateOverride = (
    id: string,
    content: string,
    status: SpecialConditionStatus
  ) =>
    update({
      vendorId: vendorId,
      id: id,
      body: {
        data: {
          type: "special-conditions",
          id: id,
          attributes: { content, status },
        } as unknown as V2SpecialConditionResource,
      },
    }).unwrap();

  return { overrides, isLoading, refetch, createOverride, updateOverride };
};

export default useVendorSpecialConditions;
